import { createContext } from 'react';
import { CommandManager } from '../../commands/CommandManager';
import {
  AppState,
  SelectionCoordinates,
  SelectionMode,
  SelectionModes,
} from '../../types';

/**
 * Narrow slice of the app state that the canvas element renderers depend on.
 *
 * Element renderers must never subscribe to the full AppStateContext: it also
 * carries high-frequency values (snap lines, hover, selection coordinates)
 * that change many times per second, and a context change re-renders every
 * subscriber regardless of React.memo on its props.
 */
export interface IElementStateContext {
  commandManager: CommandManager;
  draggingDimensionId: string | null;
  selectedElements: string[];
  selectionMode: SelectionMode;
  perpendicularGuideEnabled: boolean;
  showTriangleAngles: boolean;
  /**
   * Read imperatively from event handlers (group entry on double-click) rather
   * than during render, so element renderers do not have to subscribe to
   * appState and re-render whenever any element on the drawing changes.
   */
  elementsRef: React.MutableRefObject<AppState['elements']>;
  selectedAnchorRef: React.MutableRefObject<
    SelectionCoordinates['selectedAnchor']
  >;
  setSelectedElements: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectionMode: React.Dispatch<React.SetStateAction<SelectionMode>>;
}

export const ElementStateContext = createContext<IElementStateContext>({
  commandManager: null!,
  draggingDimensionId: null,
  selectedElements: [],
  selectionMode: { type: SelectionModes.None },
  perpendicularGuideEnabled: false,
  showTriangleAngles: false,
  elementsRef: { current: {} },
  selectedAnchorRef: { current: null },
  setSelectedElements: () => {},
  setSelectionMode: () => {},
});
